import React from "react";
import { Box, Container, Paper, Typography } from "@mui/material";
import { CalendarCheck, ShieldCheck, Sparkles, UsersRound } from "lucide-react";
import { BRAND_LOGO_MARK_URL } from "../utils/runtimeConfig";

const BRAND_ORANGE = "#ff6b00";
const BRAND_ORANGE_DARK = "#e85f00";
const SIDEBAR_BLUE = "#101828";

const defaultBenefits = [
  {
    icon: CalendarCheck,
    title: "Agenda sempre organizada",
    description: "Visualize horários, confirmações e pendências com rapidez.",
  },
  {
    icon: UsersRound,
    title: "Equipe no mesmo fluxo",
    description: "Profissionais, serviços e atendimento conectados em um só painel.",
  },
  {
    icon: ShieldCheck,
    title: "Acesso administrativo seguro",
    description: "Sessão protegida para gerenciar a operação do estabelecimento.",
  },
];

const AuthLayout = ({
  children,
  title,
  subtitle,
  showBrandPanel = false,
  eyebrow = "FilaZero Admin",
  benefits = defaultBenefits,
}) => {
  const card = (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "18px",
        p: { xs: 3, sm: 4 },
        width: "100%",
        border: "1px solid rgba(16, 24, 40, 0.08)",
        boxShadow: "0 24px 70px rgba(16, 24, 40, 0.12)",
        backgroundColor: "#ffffff",
      }}
    >
      <Box sx={{ textAlign: "center", mb: 3 }}>
        <Box
          component="img"
          src={BRAND_LOGO_MARK_URL}
          alt="FilaZero"
          sx={{
            width: 72,
            height: 72,
            borderRadius: "18px",
            mb: 2,
            boxShadow: "0 16px 34px rgba(16, 24, 40, 0.18)",
          }}
        />

        <Typography
          component="span"
          sx={{
            display: "block",
            color: BRAND_ORANGE,
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            mb: 0.75,
          }}
        >
          {eyebrow}
        </Typography>

        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            color: SIDEBAR_BLUE,
            mb: 1,
            lineHeight: 1.15,
          }}
        >
          {title || "FilaZero Admin"}
        </Typography>

        {subtitle && (
          <Typography
            variant="body2"
            sx={{
              color: "#667085",
              maxWidth: 360,
              mx: "auto",
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>

      <Box>{children}</Box>
    </Paper>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: { xs: 2, sm: 4 },
        background:
          "radial-gradient(circle at 18% 18%, rgba(255, 107, 0, 0.10), transparent 28%), linear-gradient(135deg, #f8fafc 0%, #eef2f7 100%)",
      }}
    >
      <Container maxWidth={showBrandPanel ? "lg" : "xs"} disableGutters={showBrandPanel}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: showBrandPanel
              ? { xs: "1fr", md: "minmax(390px, 0.88fr) minmax(420px, 1fr)" }
              : "1fr",
            alignItems: "stretch",
            gap: { xs: 2.5, md: 0 },
            mx: { xs: 0, md: showBrandPanel ? 3 : 0 },
          }}
        >
          <Box sx={{ maxWidth: 496, width: "100%", mx: "auto", zIndex: 1 }}>{card}</Box>

          {showBrandPanel && (
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                flexDirection: "column",
                justifyContent: "space-between",
                minHeight: 620,
                p: 5,
                borderRadius: "0 22px 22px 0",
                color: "#fff",
                background: `linear-gradient(145deg, ${SIDEBAR_BLUE}, #172033 58%, #1d2939)`,
                boxShadow: "0 24px 70px rgba(16, 24, 40, 0.18)",
                position: "relative",
                overflow: "hidden",
                ml: -2,
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: "auto -120px -140px auto",
                  width: 320,
                  height: 320,
                  borderRadius: "50%",
                  background: "rgba(255, 107, 0, 0.18)",
                  filter: "blur(8px)",
                }}
              />

              <Box sx={{ position: "relative", zIndex: 1 }}>
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, mb: 4 }}>
                  <Sparkles size={18} color={BRAND_ORANGE} />
                  <Typography sx={{ fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase", fontSize: 12 }}>
                    Operação inteligente
                  </Typography>
                </Box>

                <Typography
                  component="h2"
                  sx={{
                    fontSize: { md: 42, lg: 48 },
                    lineHeight: 1.04,
                    fontWeight: 800,
                    letterSpacing: 0,
                    maxWidth: 470,
                    mb: 2,
                  }}
                >
                  Menos fila. Mais controle. Atendimento que flui.
                </Typography>

                <Typography sx={{ color: "#d0d5dd", fontSize: 16, maxWidth: 440, lineHeight: 1.65 }}>
                  Entre para acompanhar a rotina do estabelecimento, resolver pendências e manter a equipe alinhada.
                </Typography>
              </Box>

              <Box sx={{ display: "grid", gap: 1.5, position: "relative", zIndex: 1 }}>
                {benefits.map(({ icon: Icon, title: benefitTitle, description }) => (
                  <Box
                    key={benefitTitle}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "42px 1fr",
                      gap: 1.5,
                      alignItems: "center",
                      p: 2,
                      borderRadius: "12px",
                      border: "1px solid rgba(255, 255, 255, 0.10)",
                      background: "rgba(255, 255, 255, 0.06)",
                    }}
                  >
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        display: "grid",
                        placeItems: "center",
                        borderRadius: "10px",
                        background: "rgba(255, 107, 0, 0.16)",
                        color: BRAND_ORANGE,
                      }}
                    >
                      <Icon size={20} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 800 }}>{benefitTitle}</Typography>
                      <Typography sx={{ color: "#d0d5dd", fontSize: 13 }}>{description}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>

        <Typography
          variant="caption"
          sx={{
            display: "block",
            textAlign: "center",
            mt: 3,
            color: "#667085",
          }}
        >
          © {new Date().getFullYear()} FilaZero Admin. Todos os direitos reservados.
        </Typography>
      </Container>
    </Box>
  );
};

export default AuthLayout;
export { BRAND_ORANGE, BRAND_ORANGE_DARK, SIDEBAR_BLUE };
